import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Zap, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatNumber(n) {
  return (n ?? 0).toLocaleString();
}

export default function QuotaBar({ refreshKey = 0 }) {
  const { t } = useLanguage();
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchQuota = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/quota`);
      setQuota(data);
    } catch {
      setQuota(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuota();
  }, [fetchQuota, refreshKey]);

  if (loading || !quota) {
    return (
      <div
        data-testid="quota-bar-loading"
        className="rounded-md border border-zinc-200 bg-zinc-50/60 px-3 py-2.5 flex items-center gap-2"
      >
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-200">
          <div className="h-full w-1/4 animate-pulse bg-zinc-300" />
        </div>
      </div>
    );
  }

  const dailyPct = Math.min(
    100,
    Math.round((quota.daily_chars_used / quota.daily_chars_limit) * 100)
  );
  const remaining = Math.max(
    0,
    quota.daily_chars_limit - quota.daily_chars_used
  );
  const isNearLimit = dailyPct >= 80;
  const isWarn = dailyPct >= 60 && dailyPct < 80;

  const barColor = isNearLimit
    ? "bg-red-500"
    : isWarn
    ? "bg-amber-500"
    : "bg-[#002FA7]";
  const borderColor = isNearLimit
    ? "border-red-200 bg-red-50/70"
    : isWarn
    ? "border-amber-200 bg-amber-50/70"
    : "border-zinc-200 bg-zinc-50/60";

  return (
    <div
      data-testid="quota-bar"
      className={`rounded-md border ${borderColor} px-3 py-2.5 space-y-2 transition-colors duration-300`}
    >
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-600 font-semibold">
          <Zap
            className={`h-3 w-3 ${
              isNearLimit
                ? "text-red-600"
                : isWarn
                ? "text-amber-600"
                : "text-[#002FA7]"
            }`}
          />
          {t("quota.label")}
        </span>
        <span
          data-testid="quota-remaining"
          className="font-mono text-[11px] tabular-nums text-zinc-700 font-semibold"
        >
          {t("quota.remaining", { n: formatNumber(remaining) })}
        </span>
      </div>

      <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-200">
        <div
          data-testid="quota-progress"
          className={`h-full ${barColor} transition-all duration-500`}
          style={{ width: `${Math.max(2, dailyPct)}%` }}
        />
      </div>

      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] text-zinc-500 tabular-nums">
          {t("quota.chars", {
            used: formatNumber(quota.daily_chars_used),
            limit: formatNumber(quota.daily_chars_limit),
          })}
        </span>
        {isNearLimit ? (
          <a
            href="#precios"
            data-testid="quota-upgrade-link"
            className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest font-semibold text-red-700 hover:text-red-900 transition-colors"
          >
            {t("quota.upgradeAuthor")} <ArrowUpRight className="h-3 w-3" />
          </a>
        ) : isWarn ? (
          <a
            href="#precios"
            data-testid="quota-upgrade-link"
            className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest font-semibold text-amber-700 hover:text-amber-900 transition-colors"
          >
            {t("quota.seePlans")} <ArrowUpRight className="h-3 w-3" />
          </a>
        ) : (
          <span className="font-mono text-[10px] text-zinc-400 tabular-nums">
            {t("quota.queued", {
              active: quota.active_jobs,
              max: quota.max_active_jobs,
            })}
          </span>
        )}
      </div>
    </div>
  );
}
